import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PublicFiche } from '../../types';

interface RGPDSectionProps {
  fiche: PublicFiche;
}

const ISSUE_CONFIG = {
  gagne:      { labelKey: 'fiche.card.issue.gagne',      icon: '✓', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-300' },
  perd:       { labelKey: 'fiche.card.issue.perd',       icon: '✗', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-300' },
  partiel:    { labelKey: 'fiche.card.issue.partiel',    icon: '◑', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-300' },
  non_statue: { labelKey: 'fiche.card.issue.non_statue', icon: '—', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 border-zinc-300' },
} as const;

export const RGPDSection = ({ fiche }: RGPDSectionProps) => {
  const { t } = useTranslation();
  const hasAny = fiche.rgpd_sous_theme || fiche.rgpd_base_legale ||
    fiche.rgpd_type_donnees || fiche.rgpd_responsable_type ||
    fiche.rgpd_issue_personne || (fiche.rgpd_mots_cles?.length ?? 0) > 0;

  if (!hasAny) return null;

  const issue = fiche.rgpd_issue_personne
    ? ISSUE_CONFIG[fiche.rgpd_issue_personne as keyof typeof ISSUE_CONFIG]
    : null;

  return (
    <div className="px-5 pt-2.5 pb-0">
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Badge RGPD principal */}
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white">
          <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          </svg>
          {t('fiche.card.gdpr')}
        </span>

        {/* Sous-thème */}
        {fiche.rgpd_sous_theme && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 font-medium">
            {fiche.rgpd_sous_theme}
          </span>
        )}

        {/* Base légale */}
        {fiche.rgpd_base_legale && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border border-violet-200 font-medium">
            {fiche.rgpd_base_legale.replace(/_/g, ' ')}
          </span>
        )}

        {/* Type de données */}
        {fiche.rgpd_type_donnees && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 border border-cyan-200">
            {fiche.rgpd_type_donnees}
          </span>
        )}

        {/* Responsable type */}
        {fiche.rgpd_responsable_type && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200">
            {fiche.rgpd_responsable_type}
          </span>
        )}

        {/* Issue personne */}
        {issue && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${issue.color}`}>
            <span aria-hidden="true">{issue.icon}</span>
            {t(issue.labelKey)}
          </span>
        )}

        {/* Mots-clés RGPD */}
        {(fiche.rgpd_mots_cles ?? []).slice(0, 3).map((kw, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {kw}
          </span>
        ))}
        {(fiche.rgpd_mots_cles?.length ?? 0) > 3 && (
          <span className="text-[10px] text-muted-foreground">
            +{(fiche.rgpd_mots_cles?.length ?? 0) - 3}
          </span>
        )}
      </div>
    </div>
  );
};